import * as React from 'react';
import { Transition, Dialog } from '@headlessui/react';
import { FeatherIcon } from '../../../components/FeatherIcon';
import { ConfirmationModalContent } from '../../../components/ConfirmationModalContent';
import { DialogButton } from '../../../components/DialogButton';
import { DialogContentContainer } from '../../../components/DialogContentContainer';

interface ImageMenuProps {
  imageId: string;
  deleteImage: (id: string) => Promise<void>;
  setHighlight: (id: string) => Promise<void>;
}

const overlayButtonStyle =
  'text-white p-2 rounded-full bg-grey-800 bg-opacity-10 hover:bg-opacity-20 flex items-center justify-center focus:outline-none';

interface DeleteImageState {
  open: boolean;
  confirming: boolean;
}

type Action = 'CloseMenu' | 'OpenMenu' | 'DisplayConfirm';

const reducer = (state: DeleteImageState, action: Action): DeleteImageState => {
  if (action === 'OpenMenu') {
    return { open: true, confirming: false };
  }
  if (action === 'DisplayConfirm') {
    return { open: true, confirming: true };
  }
  if (action === 'CloseMenu') {
    return { open: false, confirming: false };
  }

  return state;
};

export const MatchPhotoOverlayMenu: React.FC<ImageMenuProps> = ({
  imageId,
  deleteImage,
  setHighlight,
}) => {
  const [state, dispatch] = React.useReducer(reducer, {
    open: false,
    confirming: false,
  });

  const onDeleteImage = () => {
    dispatch('CloseMenu');
    deleteImage(imageId);
  };

  const onSetFeatured = () => {
    dispatch('CloseMenu');
    setHighlight(imageId);
  };

  return (
    <div className="text-white">
      <button
        onClick={() => dispatch('OpenMenu')}
        className={overlayButtonStyle}
      >
        <FeatherIcon icon="more-horizontal" aria-label="Image actions" />
      </button>

      <Transition appear show={state.open} as={React.Fragment}>
        <Dialog
          onClose={() => dispatch('CloseMenu')}
          as="div"
          className="fixed inset-0 z-20 overflow-y-auto flex justify-center items-center"
        >
          <Dialog.Overlay className="fixed inset-0 bg-grey-700 bg-opacity-60 backdrop-filter backdrop-blur-sm" />
          <div className="max-w-sm w-full px-4">
            <DialogContentContainer className="w-full max-w-sm">
              {state.confirming ? (
                <ConfirmationModalContent
                  title="Delete Photo"
                  onOk={onDeleteImage}
                  onCancel={() => dispatch('CloseMenu')}
                  destructive
                  okButtonText="Delete"
                >
                  Are you sure you want to delete this photo?
                </ConfirmationModalContent>
              ) : (
                <ul className="divide-y divide-grey-200">
                  <li>
                    <DialogButton
                      className="font-semibold text-red-700"
                      onClick={() => dispatch('DisplayConfirm')}
                    >
                      Delete Photo
                    </DialogButton>
                  </li>
                  <li>
                    <DialogButton onClick={onSetFeatured}>
                      Set as Highlight Photo
                    </DialogButton>
                  </li>
                </ul>
              )}
            </DialogContentContainer>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};
